<template>
  <v-container fluid class="pa-0" fill-height>
    <v-card
      flat
      tile
      :disabled="$store.state.editor.drawer.loading"
      color="transparent"
      height="100%"
      width="100%"
    >
      <v-card-text class="preview-wrapper">
        <div
          class="frontend-main-body section-builder-draggable section-builder-preview"
          :class="`page-${slug}`"
        >
          <v-fade-transition mode="out-in">
            <div class="drop-zone" v-if="!sections.length">
              <h1>Drag & drop or click a section to add.</h1>
              <draggable
                v-model="sections"
                v-bind="dropZoneOptions"
                style="height: 100%;"
              ></draggable>
            </div>

            <draggable
              :class="[`text-color-${templateSettings.design['text-color']}`]"
              v-model="sections"
              v-bind="draggableOptions"
              v-else
            >
              <transition-group type="transition" name="flip-list">
                <div
                  v-for="(section, index) in sections"
                  :key="`section-${index}`"
                  class="section-wrapper"
                  :class="sectionClasses(section)"
                  :id="`section-${section.id ? section.id : 'new'}`"
                >
                  <component
                    :is="section.type"
                    :section="section"
                    :templateSettings="templateSettings"
                  ></component>

                  <section-editor-toolbar
                    :section="section"
                    :pageID="pageBySlug.id"
                  ></section-editor-toolbar>
                </div>
              </transition-group>
            </draggable>
          </v-fade-transition>
        </div>

        <div
          id="ghost-sections"
          class="frontend-main-body"
          :class="`page-${slug}`"
        ></div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import SectionEditorToolbar from '@/components/sections/SectionEditorToolbar'
import SectionComponents from '@/components/sections/types'
import { activeTemplate } from '@/graphql/template.gql'
import { pageBySlug, buildPageSections } from '@/graphql/page.gql'
import { mapActions } from 'vuex'
import draggable from 'vuedraggable'

export default {
  name: 'SectionBuilderPreview',
  components: {
    SectionEditorToolbar,
    ...SectionComponents,
    draggable
  },
  computed: {
    slug() {
      return (
        this.$route.params.page ||
        this.activeTemplate.pages.filter(page => page.home)[0].slug
      )
    },
    templateSettings() {
      let templateSettings = {
        content: {},
        design: {},
        layout: {},
        advanced: {}
      }

      if (this.activeTemplate) {
        templateSettings.content = this.activeTemplate.content
        templateSettings.design = this.activeTemplate.design
        templateSettings.layout = this.activeTemplate.layout
        templateSettings.advanced = this.activeTemplate.advanced
      }

      return templateSettings
    },
    sections: {
      get() {
        return this.pageBySlug ? this.pageBySlug.sections : []
      },
      set(value) {
        if (this.pageBySlug) {
          this.pageBySlug.sections = value

          this.buildPageSections()
        }
      }
    },
    pageSections() {
      return {
        page_id: this.pageBySlug ? this.pageBySlug.id : null,
        sections: this.sections.map((section, index) => {
          let sections = {
            id: section.id,
            order: index + 1
          }

          if (!section.id) {
            sections = {
              id: null,
              order: index + 1,
              global: section.global,
              type: section.type,
              content: section.content,
              design: section.design,
              layout: section.layout,
              advanced: section.advanced
            }
          }

          return sections
        })
      }
    },
    draggableOptions() {
      return {
        animation: 200,
        group: { name: 'sections', put: true },
        swapThreshold: 0.5,
        setData: function(dataTransfer, dragEl) {
          let ghostNode = document.getElementById('ghost-sections')

          while (ghostNode.firstChild) {
            ghostNode.removeChild(ghostNode.firstChild)
          }

          // Create the clone (with content)
          let dragGhost = dragEl.cloneNode(true)
          // Stylize it
          dragGhost.classList.add('custom-drag-image')
          // dragGhost.style.width = '200%'
          dragGhost.style.zoom = 0.5
          // Place it into the DOM tree
          ghostNode.appendChild(dragGhost)
          // Set the new stylized "drag image" of the dragged element
          dataTransfer.setDragImage(dragGhost, 10, 10)
        }
      }
    },
    dropZoneOptions() {
      return {
        group: 'sections',
        sort: false,
        pull: false
      }
    }
  },
  apollo: {
    activeTemplate: {
      query: activeTemplate
    },
    pageBySlug: {
      query: pageBySlug,
      skip() {
        return !this.activeTemplate
      },
      variables() {
        return {
          data: {
            slug: this.slug,
            template_id: this.activeTemplate.id
          }
        }
      }
    }
  },
  methods: {
    ...mapActions('editor', ['saveChanges']),
    sectionClasses(section) {
      let classes = section.element_classes || ''

      if (
        section.design['background-color'] == 'var(--primary-color)' ||
        (section.layout['content-box'] == '1' &&
          section.layout['content-box-color'] == 'var(--primary-color)')
      ) {
        classes += ` text-color-${this.templateSettings.design['primary-text-color']}`
      }

      if (
        section.design['background-color'] == 'var(--secondary-color)' ||
        (section.layout['content-box'] == '1' &&
          section.layout['content-box-color'] == 'var(--secondary-color)')
      ) {
        classes += ` text-color-${this.templateSettings.design['secondary-text-color']}`
      }

      return classes
    },
    buildPageSections() {
      this.saveChanges({
        mutation: buildPageSections,
        variables: {
          data: this.pageSections
        },
        messages: {
          success: 'Sections successfully updated.'
        },
        refetch: true,
        close: false
      })
    }
  }
}
</script>

<style>
.frontend-main-body.section-builder-draggable {
  background-color: transparent;
}
.frontend-main-body.section-builder-draggable {
  transform-origin: 0 0;
  -webkit-transform: scale(0.33);
  -moz-transform: scale(0.33);
  transform: scale(0.33);
  min-width: 300%;
  height: calc(300%);
  overflow: scroll;
  position: relative;
}
.section-builder-preview .section-wrapper,
.section-builder-presets .section-wrapper {
  margin: 0 auto 20px;
  cursor: pointer;
  border: 2px solid rgba(0, 0, 0, 0.2);
}
/* .section-builder-preview .section-wrapper > section,
.section-builder-presets .section-wrapper > section {
  padding-left: 40px;
  padding-right: 40px;
} */
.section-builder-preview > .section-wrapper:last-child,
.section-builder-presets .section-wrapper:last-child {
  margin-bottom: 0;
}
#ghost-sections {
  position: relative;
  z-index: -1;
}
.section-builder-preview .sortable-chosen,
.section-builder-presets .sortable-chosen {
  opacity: 0.3;
  transition: opacity 0.3s ease 0s;
}
#ghost-sections .sortable-chosen {
  opacity: 1;
}
.preview-container {
  z-index: 101;
}
.preview-wrapper {
  position: absolute;
  top: 0;
  bottom: 0;
  overflow: hidden;
}
.preview-wrapper .section-editor-toolbar {
  transform-origin: 0 100%;
  -webkit-transform: scale(1.5);
  -moz-transform: scale(1.5);
  transform: scale(1.5);
  max-width: 66.6%;
  z-index: 10;
  position: relative;
}
.drop-zone {
  position: relative;
  height: 400px;
  border: 8px dashed #aeaeae;
  margin-bottom: 20px;
  text-align: center;
}
.drop-zone h1 {
  font-size: 200%;
  width: 100%;
  position: absolute;
  top: 50%;
  -webkit-transform: translate(0, -50%);
  -moz-transform: translate(0, -50%);
  transform: translate(0, -50%);
}
</style>
